/**
 * Past Orders History — unified staff view across POS / BC / Wholesale
 * / StoreClub. Searchable, filterable, paginated, with click-through
 * to existing detail pages + a "Reopen" affordance for orders
 * picked up within ORDER_REOPEN_WINDOW_DAYS (default 7).
 *
 * Endpoint: GET /api/store/orders-history/  (see store_views.py)
 * Reopen:   POST /api/store/orders/<type>/<id>/reopen/  (BC only)
 *
 * Default window: last 30 days.
 * Default sort: newest first.
 * Page size: 50.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, useNavigationType } from 'react-router-dom';
import { useStoreAuth } from '../../contexts/StoreAuthContext';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import StoreHeader from '../../components/Layout/StoreHeader';
import api from '../../services/api';

interface OrderRow {
  id: number;
  order_number: string;
  type: 'pos' | 'bc' | 'wholesale' | 'storeclub';
  type_label: string;
  type_icon: string;
  customer_name: string;
  customer_id: number | null;
  member_code: string;
  placed_at: string | null;
  status: string;
  status_display: string;
  payment_status: string;
  fulfillment_method: string;
  fulfillment_label: string;
  total: number;
  item_count: number;
  can_reopen: boolean;
  detail_url: string;
}

interface HistoryResponse {
  orders: OrderRow[];
  page: number;
  page_size: number;
  total_pages: number;
  total_count: number;
  filtered_count: number;
  filters_applied: Record<string, any>;
  window_days: number;
  large_window_warning: boolean;
}

// Date-range presets. Custom range opens explicit date pickers.
type RangePreset = '7d' | '30d' | '90d' | 'custom';

const toIsoDate = (d: Date) => d.toISOString().slice(0, 10);

const presetToDates = (preset: RangePreset): { start: string; end: string } => {
  const end = new Date();
  const start = new Date();
  if (preset === '7d') start.setDate(start.getDate() - 7);
  else if (preset === '90d') start.setDate(start.getDate() - 90);
  else start.setDate(start.getDate() - 30);
  return { start: toIsoDate(start), end: toIsoDate(end) };
};

const ALL_ORDER_TYPES = ['pos', 'bc', 'wholesale', 'storeclub'] as const;
const ALL_FULFILLMENT = ['cashier', 'locker', 'in_store', 'will_call', 'shipped'] as const;
const FULFILLMENT_LABELS: Record<string, string> = {
  cashier: 'Cashier (POS)',
  locker: 'Locker',
  in_store: 'In-Store',
  will_call: 'Will Call',
  shipped: 'Shipped',
};

// Window scroll position saved before opening an order, restored on back-navigation.
const SCROLL_KEY = 'ordersHistoryListScrollY';

// Validate a range param, falling back to the 30-day default for anything unknown.
const parsePreset = (r: string | null): RangePreset =>
  r === '7d' || r === '90d' || r === 'custom' ? r : '30d';

// Decode a comma-separated multi-filter param into a Set, dropping any value outside
// the known domain (stale/unknown URL values are ignored, never crash). Returns null
// when the param is ABSENT so the caller applies that filter's default; an empty value
// (`types=`) decodes to an empty Set — distinct from absent, so both round-trip.
const decodeSet = (raw: string | null, domain: readonly string[]): Set<string> | null => {
  if (raw === null) return null;
  return new Set(raw.split(',').map((s) => s.trim()).filter((v) => domain.includes(v)));
};

// Status pill color per common status. Falls through to grey.
const statusColor = (status: string): string => {
  const s = status.toLowerCase();
  if (s.includes('picked_up') || s.includes('shipped') || s === 'delivered' || s === 'completed') {
    return '#9CA3AF';
  }
  if (s.includes('ready')) return '#10B981';
  if (s.includes('paid')) return '#3B82F6';
  if (s.includes('cancel') || s.includes('voided') || s.includes('refunded')) {
    return '#DC2626';
  }
  if (s.includes('pending') || s.includes('await')) return '#F59E0B';
  if (s.includes('production') || s.includes('milled') || s.includes('packaging')) {
    return '#8B5CF6';
  }
  return '#6B7280';
};

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
      timeZone: 'America/Los_Angeles',
    });
  } catch {
    return iso;
  }
};

const OrdersHistory: React.FC = () => {
  const { isAuthenticated } = useStoreAuth();
  const navigate = useNavigate();
  const navType = useNavigationType();
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState<HistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter state — all seeded from the URL so refresh + back-navigation restore it.
  const [rangePreset, setRangePreset] = useState<RangePreset>(() => parsePreset(searchParams.get('range')));
  const [startDate, setStartDate] = useState(() => {
    const p = parsePreset(searchParams.get('range'));
    return p === 'custom' ? (searchParams.get('start') || presetToDates('30d').start) : presetToDates(p).start;
  });
  const [endDate, setEndDate] = useState(() => {
    const p = parsePreset(searchParams.get('range'));
    return p === 'custom' ? (searchParams.get('end') || presetToDates('30d').end) : presetToDates(p).end;
  });
  // Multi-filters are Sets. `types` defaults to ALL (absent param); `fulfillment`
  // defaults to EMPTY (absent param). decodeSet drops unknown/stale values.
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(
    () => decodeSet(searchParams.get('types'), ALL_ORDER_TYPES) ?? new Set(ALL_ORDER_TYPES),
  );
  const [selectedFulfillment, setSelectedFulfillment] = useState<Set<string>>(
    () => decodeSet(searchParams.get('fulfillment'), ALL_FULFILLMENT) ?? new Set(),
  );
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || '');
  // Server search: input binds to `searchInput` (instant); debounced `search` (400ms)
  // drives fetchHistory so typing fires one request after the pause. Seeded from ?q=.
  const { value: searchInput, setValue: setSearchInput, debounced: search } =
    useDebouncedSearch(searchParams.get('q') || '', 400);
  const [sort, setSort] = useState(() => searchParams.get('sort') || 'date_desc');
  const [page, setPage] = useState(() => Number(searchParams.get('page')) || 1);

  // Reopen state
  const [reopenTarget, setReopenTarget] = useState<OrderRow | null>(null);
  const [reopenSubmitting, setReopenSubmitting] = useState(false);
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const showToast = (kind: 'success' | 'error', text: string) => {
    setToast({ kind, text });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, string> = {
        start_date: startDate,
        end_date: endDate,
        page: String(page),
        page_size: '50',
        sort,
      };
      if (selectedTypes.size && selectedTypes.size < ALL_ORDER_TYPES.length) {
        params.order_types = Array.from(selectedTypes).join(',');
      }
      if (selectedFulfillment.size) {
        params.fulfillment_methods = Array.from(selectedFulfillment).join(',');
      }
      if (statusFilter) params.statuses = statusFilter;
      if (search.trim()) params.search = search.trim();

      const qs = new URLSearchParams(params).toString();
      const res = await api.get(`/store/orders-history/?${qs}`);
      setData(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load orders history');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedTypes, selectedFulfillment, statusFilter, search, sort, page]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/store/login');
      return;
    }
    fetchHistory();
  }, [isAuthenticated, fetchHistory, navigate]);

  // Mirror ALL list filters into the URL so refresh + back-navigation restore them.
  // Keyed on the DEBOUNCED search (`search`), so the URL updates once per pause, not
  // per keystroke. `replace: true` means filtering doesn't stack a history entry —
  // browser-back leaves the list, it doesn't rewind through each filter change.
  // Set encoding: `types` is omitted when ALL are selected (its default) and written
  // as a bare `types=` when empty, so ALL / empty / subset stay distinguishable;
  // `fulfillment` is omitted when empty (its default) and written when non-empty.
  useEffect(() => {
    const next = new URLSearchParams();
    next.set('range', rangePreset);
    if (rangePreset === 'custom') {
      next.set('start', startDate);
      next.set('end', endDate);
    }
    if (selectedTypes.size < ALL_ORDER_TYPES.length) {
      next.set('types', Array.from(selectedTypes).join(','));
    }
    if (selectedFulfillment.size) {
      next.set('fulfillment', Array.from(selectedFulfillment).join(','));
    }
    if (statusFilter) next.set('status', statusFilter);
    if (search) next.set('q', search);
    if (sort !== 'date_desc') next.set('sort', sort);
    if (page > 1) next.set('page', String(page));
    setSearchParams(next, { replace: true });
  }, [rangePreset, startDate, endDate, selectedTypes, selectedFulfillment, statusFilter, search, sort, page, setSearchParams]);

  // Navigate into an order, saving the list's scroll position first so we can restore
  // it on the way back. Every entry point into an order goes through this.
  const goToOrder = useCallback((url: string) => {
    try { sessionStorage.setItem(SCROLL_KEY, String(window.scrollY)); } catch { /* ignore */ }
    navigate(url);
  }, [navigate]);

  // Capture the navigation type AT MOUNT. The URL-sync effect above calls
  // setSearchParams(replace) on mount, which flips useNavigationType() to 'REPLACE' —
  // so reading it later in the restore would wrongly conclude this wasn't a back/forward
  // (POP) navigation and skip the restore. The ref freezes the first-render value.
  const arrivedViaPop = useRef(navType === 'POP').current;

  // Restore the saved scroll position on BACK-navigation, AFTER the rows have rendered
  // (keyed on `loading` going false). The rAF runs after the app's <ScrollToTop> mount
  // rAF, so our restore wins. One-shot: the key is consumed on restore.
  const didRestoreScroll = useRef(false);
  useEffect(() => {
    if (loading || didRestoreScroll.current) return;
    didRestoreScroll.current = true;
    const saved = sessionStorage.getItem(SCROLL_KEY);
    sessionStorage.removeItem(SCROLL_KEY);
    if (arrivedViaPop && saved) {
      requestAnimationFrame(() => window.scrollTo(0, parseInt(saved, 10)));
    }
  }, [loading, arrivedViaPop]);

  const handlePresetChange = (preset: RangePreset) => {
    setRangePreset(preset);
    if (preset !== 'custom') {
      const { start, end } = presetToDates(preset);
      setStartDate(start);
      setEndDate(end);
    }
    setPage(1);
  };

  const toggleType = (t: string) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
    setPage(1);
  };

  const toggleFulfillment = (f: string) => {
    setSelectedFulfillment((prev) => {
      const next = new Set(prev);
      if (next.has(f)) next.delete(f);
      else next.add(f);
      return next;
    });
    setPage(1);
  };

  const handleRowClick = (row: OrderRow) => {
    if (row.detail_url) {
      goToOrder(row.detail_url);
    } else {
      showToast(
        'error',
        `No detail view available for ${row.type_label} orders yet`,
      );
    }
  };

  const handleReopen = (row: OrderRow) => {
    setReopenTarget(row);
  };

  const confirmReopen = async () => {
    if (!reopenTarget) return;
    setReopenSubmitting(true);
    try {
      await api.post(
        `/api/store/orders/${reopenTarget.type}/${reopenTarget.id}/reopen/`,
      );
      showToast(
        'success',
        `Order ${reopenTarget.order_number} reopened — back to ready for pickup`,
      );
      setReopenTarget(null);
      fetchHistory();
    } catch (err: any) {
      showToast(
        'error',
        err.response?.data?.error || 'Failed to reopen order',
      );
      setReopenTarget(null);
    } finally {
      setReopenSubmitting(false);
    }
  };

  const clearAllFilters = () => {
    setSelectedTypes(new Set(ALL_ORDER_TYPES));
    setSelectedFulfillment(new Set());
    setStatusFilter('');
    setSearchInput('');
    setRangePreset('30d');
    const r = presetToDates('30d');
    setStartDate(r.start);
    setEndDate(r.end);
    setPage(1);
  };

  // Derived UI state.
  const showingFrom = data && data.filtered_count > 0
    ? (data.page - 1) * data.page_size + 1
    : 0;
  const showingTo = data
    ? Math.min(data.page * data.page_size, data.filtered_count)
    : 0;

  if (!isAuthenticated) return null;

  return (
    <div style={styles.page}>
      <StoreHeader />
      <main style={styles.main}>
        <div style={styles.header}>
          <h1 style={styles.title}>Past Orders</h1>
          <p style={styles.subtitle}>
            Unified history — POS, Bridge Club, Wholesale, Store Club
          </p>
        </div>

        {/* ── Filters ────────────────────────────────────────────── */}
        <div style={styles.filters}>
          {/* Date range presets */}
          <div style={styles.filterRow}>
            <span style={styles.filterLabel}>Date range:</span>
            {(['7d', '30d', '90d', 'custom'] as RangePreset[]).map((p) => (
              <button
                key={p}
                onClick={() => handlePresetChange(p)}
                style={{
                  ...styles.pillBtn,
                  ...(rangePreset === p ? styles.pillBtnActive : {}),
                }}
              >
                {p === '7d' ? 'Last 7 days' :
                 p === '30d' ? 'Last 30 days' :
                 p === '90d' ? 'Last 90 days' : 'Custom'}
              </button>
            ))}
            {rangePreset === 'custom' && (
              <>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                  style={styles.dateInput}
                />
                <span>→</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                  style={styles.dateInput}
                />
              </>
            )}
          </div>

          {/* Type checkboxes */}
          <div style={styles.filterRow}>
            <span style={styles.filterLabel}>Type:</span>
            {ALL_ORDER_TYPES.map((t) => (
              <label key={t} style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={selectedTypes.has(t)}
                  onChange={() => toggleType(t)}
                />
                <span style={{ marginLeft: 4 }}>
                  {t === 'pos' ? '🛒 POS' :
                   t === 'bc' ? '📦 BC Online' :
                   t === 'wholesale' ? '🏭 Wholesale' :
                   '🛍 Store Club'}
                </span>
              </label>
            ))}
          </div>

          {/* Fulfillment + search row */}
          <div style={styles.filterRow}>
            <span style={styles.filterLabel}>Fulfillment:</span>
            {ALL_FULFILLMENT.map((f) => (
              <label key={f} style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={selectedFulfillment.has(f)}
                  onChange={() => toggleFulfillment(f)}
                />
                <span style={{ marginLeft: 4 }}>{FULFILLMENT_LABELS[f]}</span>
              </label>
            ))}
          </div>

          <div style={styles.filterRow}>
            <input
              type="text"
              placeholder="Search by order #, customer name, or member code…"
              value={searchInput}
              onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
              style={styles.searchInput}
            />
            <select
              value={sort}
              onChange={(e) => { setSort(e.target.value); setPage(1); }}
              style={styles.sortSelect}
            >
              <option value="date_desc">Newest first</option>
              <option value="date_asc">Oldest first</option>
              <option value="customer_asc">Customer (A-Z)</option>
              <option value="total_desc">Total (high to low)</option>
              <option value="status_asc">Status (A-Z)</option>
            </select>
            <button onClick={clearAllFilters} style={styles.clearBtn}>
              Clear filters
            </button>
          </div>
        </div>

        {/* ── Result count + warning ─────────────────────────────── */}
        {data && (
          <div style={styles.resultMeta}>
            <span>
              Showing <strong>{showingFrom}-{showingTo}</strong> of{' '}
              <strong>{data.filtered_count}</strong> orders
            </span>
            {data.large_window_warning && (
              <span style={styles.warningInline}>
                ⚠ Wide date range — narrow it for faster results.
              </span>
            )}
          </div>
        )}

        {/* ── Results table ──────────────────────────────────────── */}
        {loading && !data ? (
          <div style={styles.loadingState}>Loading…</div>
        ) : error ? (
          <div style={styles.errorState}>{error}</div>
        ) : data && data.orders.length === 0 ? (
          <div style={styles.emptyState}>
            No orders match your filters. Try widening the date range or
            clearing filters.
          </div>
        ) : data ? (
          <>
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Order #</th>
                    <th style={styles.th}>Customer</th>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Payment</th>
                    <th style={styles.th}>Fulfillment</th>
                    <th style={styles.thRight}>Total</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.orders.map((row) => (
                    <tr key={`${row.type}-${row.id}`} style={styles.tr}>
                      <td style={styles.td}>
                        <span title={row.type_label} style={{ marginRight: 4 }}>
                          {row.type_icon}
                        </span>
                        <button
                          onClick={() => handleRowClick(row)}
                          style={styles.orderLink}
                          disabled={!row.detail_url}
                          title={row.detail_url ? 'View details' : 'No detail view yet'}
                        >
                          {row.order_number}
                        </button>
                      </td>
                      <td style={styles.td}>
                        <div>{row.customer_name}</div>
                        {row.member_code && (
                          <div style={styles.memberCode}>{row.member_code}</div>
                        )}
                      </td>
                      <td style={styles.td}>{formatDateTime(row.placed_at)}</td>
                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.statusPill,
                            background: statusColor(row.status),
                          }}
                        >
                          {row.status_display}
                        </span>
                      </td>
                      <td style={styles.td}>{row.payment_status}</td>
                      <td style={styles.td}>{row.fulfillment_label}</td>
                      <td style={styles.tdRight}>${row.total.toFixed(2)}</td>
                      <td style={styles.td}>
                        {row.can_reopen && (
                          <button
                            onClick={() => handleReopen(row)}
                            style={styles.reopenBtn}
                            title="Revert this order to ready_for_pickup"
                          >
                            Reopen
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data.total_pages > 1 && (
              <div style={styles.pagination}>
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  style={styles.pageBtn}
                >
                  ← Previous
                </button>
                <span style={styles.pageInfo}>
                  Page {data.page} of {data.total_pages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))}
                  disabled={page === data.total_pages}
                  style={styles.pageBtn}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        ) : null}
      </main>

      {/* ── Reopen confirmation modal ────────────────────────────── */}
      {reopenTarget && (
        <div
          style={styles.modalOverlay}
          onClick={() => !reopenSubmitting && setReopenTarget(null)}
        >
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Reopen Order?</h2>
            <p style={styles.modalText}>
              Revert Order <strong>{reopenTarget.order_number}</strong> back to
              ready for pickup?
            </p>
            <ul style={styles.modalList}>
              <li>Status: <code>picked_up</code> → <code>ready_for_pickup</code></li>
              <li>Pickup timestamp cleared</li>
              <li>Locker assignment + camera snapshot preserved for audit</li>
              <li>Audit log entry recorded with your username</li>
            </ul>
            <p style={styles.modalWarning}>
              ⚠ Customer is NOT auto-notified of the correction. Reach out
              manually if needed.
            </p>
            <div style={styles.modalActions}>
              <button
                onClick={() => setReopenTarget(null)}
                disabled={reopenSubmitting}
                style={styles.cancelBtn}
              >
                Cancel
              </button>
              <button
                onClick={confirmReopen}
                disabled={reopenSubmitting}
                style={styles.confirmBtn}
              >
                {reopenSubmitting ? 'Reopening…' : 'Yes, Reopen Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ────────────────────────────────────────────────── */}
      {toast && (
        <div
          style={{
            ...styles.toast,
            background: toast.kind === 'success' ? '#ECFDF5' : '#FEF2F2',
            color: toast.kind === 'success' ? '#065F46' : '#991B1B',
            borderColor: toast.kind === 'success' ? '#10B981' : '#FCA5A5',
          }}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  page: { minHeight: '100vh', backgroundColor: '#f5f5f5' },
  main: { maxWidth: '1400px', margin: '0 auto', padding: '1.5rem' },
  header: { marginBottom: '1rem' },
  title: { fontSize: '1.5rem', fontWeight: 'bold', color: '#1a4d1a', margin: 0 },
  subtitle: { color: '#666', fontSize: 14, margin: '4px 0 0 0' },
  filters: {
    background: '#fff',
    padding: 16,
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    marginBottom: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  filterRow: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 },
  filterLabel: { fontWeight: 600, fontSize: 13, color: '#374151', minWidth: 90 },
  pillBtn: {
    padding: '6px 12px',
    borderRadius: 999,
    border: '1px solid #d1d5db',
    background: 'white',
    fontSize: 13,
    cursor: 'pointer',
  },
  pillBtnActive: {
    background: '#1a4d1a',
    color: 'white',
    borderColor: '#1a4d1a',
  },
  dateInput: {
    padding: '6px 8px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: 13,
  },
  checkboxLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 13,
    cursor: 'pointer',
    padding: '4px 8px',
  },
  searchInput: {
    flex: 1,
    minWidth: 300,
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: 14,
  },
  sortSelect: {
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: 14,
    background: 'white',
  },
  clearBtn: {
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    background: '#f9fafb',
    color: '#374151',
    borderRadius: 6,
    fontSize: 13,
    cursor: 'pointer',
  },
  resultMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 13,
    color: '#6b7280',
    marginBottom: 10,
    padding: '0 4px',
  },
  warningInline: {
    color: '#92400e',
    background: '#fef3c7',
    padding: '2px 8px',
    borderRadius: 4,
  },
  loadingState: {
    padding: '3rem',
    textAlign: 'center',
    color: '#6b7280',
    background: '#fff',
    borderRadius: 8,
  },
  errorState: {
    padding: '1rem',
    background: '#fef2f2',
    color: '#991b1b',
    borderRadius: 8,
    border: '1px solid #fca5a5',
  },
  emptyState: {
    padding: '3rem',
    textAlign: 'center',
    color: '#6b7280',
    background: '#fff',
    borderRadius: 8,
  },
  tableWrapper: {
    background: '#fff',
    borderRadius: 8,
    overflow: 'hidden',
    border: '1px solid #e5e7eb',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    padding: '10px 12px',
    fontSize: 12,
    fontWeight: 600,
    color: '#374151',
    textAlign: 'left',
    background: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
    textTransform: 'uppercase',
  },
  thRight: {
    padding: '10px 12px',
    fontSize: 12,
    fontWeight: 600,
    color: '#374151',
    textAlign: 'right',
    background: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
    textTransform: 'uppercase',
  },
  tr: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '10px 12px', fontSize: 13, color: '#111827', verticalAlign: 'top' },
  tdRight: { padding: '10px 12px', fontSize: 13, color: '#111827', textAlign: 'right' },
  orderLink: {
    background: 'none',
    border: 'none',
    color: '#1a4d1a',
    textDecoration: 'underline',
    cursor: 'pointer',
    padding: 0,
    fontSize: 13,
    fontWeight: 600,
  },
  memberCode: { fontSize: 11, color: '#9ca3af', marginTop: 2 },
  statusPill: {
    color: 'white',
    padding: '2px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    display: 'inline-block',
  },
  reopenBtn: {
    padding: '4px 10px',
    background: '#f59e0b',
    color: 'white',
    border: 'none',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
    padding: 16,
  },
  pageBtn: {
    padding: '6px 12px',
    border: '1px solid #d1d5db',
    background: 'white',
    borderRadius: 6,
    fontSize: 13,
    cursor: 'pointer',
  },
  pageInfo: { fontSize: 13, color: '#374151' },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  modal: {
    background: 'white',
    borderRadius: 12,
    padding: 24,
    maxWidth: 480,
    width: '90%',
  },
  modalTitle: { fontSize: 18, fontWeight: 700, marginTop: 0, color: '#1a4d1a' },
  modalText: { fontSize: 14, color: '#374151', lineHeight: 1.6 },
  modalList: { fontSize: 13, color: '#374151', lineHeight: 1.8, paddingLeft: 20 },
  modalWarning: {
    fontSize: 13,
    color: '#92400e',
    background: '#fef3c7',
    padding: 10,
    borderRadius: 6,
    marginTop: 12,
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 20,
  },
  cancelBtn: {
    padding: '8px 16px',
    border: '1px solid #d1d5db',
    background: 'white',
    borderRadius: 6,
    fontSize: 14,
    cursor: 'pointer',
  },
  confirmBtn: {
    padding: '8px 16px',
    border: 'none',
    background: '#f59e0b',
    color: 'white',
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  toast: {
    position: 'fixed',
    top: 24,
    right: 24,
    padding: '10px 14px',
    border: '1px solid',
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 500,
    zIndex: 10000,
    maxWidth: 360,
  },
};

export default OrdersHistory;
